/* Global idChercheurActif, globalIdProjet */
/**
 * @file JS - Class - Project.js
 * @author Adam Lemire
 * @since 13/06/2019
 * @version 1.0
 * @summary Dernières modifications : 2023/02/17
 */

import SingleMapPage from './Page/SingleMapPage.js';
import MultiMapPage from './Page/MultiMapPage.js';
import { PageType } from './Page/Page.js'; // eslint-disable-line no-unused-vars
import { MultiMapPageFormat } from '../helpers/Constants.js'; // eslint-disable-line no-unused-vars

/**
 * @classdesc Classe Project, décrit un projet ouvert dans le SIG
 * @example
 * let monProjet = new Project({
 * title: 'mon projet en exemple',
 * gisVersion: 'iBalsac 1.0'
 * })
 */
export default class Project {
  /**
   * @param { object } options
   * @param { string } options.title - Titre du projet.
   * @param { string } options.gisVersion - Version du SIG.
   * @param { Date } options.creationDate - Date de création du projet
   * @param { Date } options.saveDate - Date de sauvegarde du projet
   * @param { Map < number, SingleMapPage | MultiMapPage > } options.mapPages - Array des cartes (SingleMapPage, ...).
   * @param { SingleMapPage | MultiMapPage } options.activeMapPage - Carte active (SingleMapPage, ...).
   * @param { number } options.pageCount
   * @param { JSON } options.jsonObject
   */
  constructor ({
    title = '',
    gisVersion,
    creationDate,
    saveDate,
    mapPages,
    activeMapPage,
    pageCount,
    jsonObject = false
  }) {
    if (jsonObject) {
      this._créerDepuisJSON(jsonObject);
    } else {
      /** @type { string } */
      this._titre = title;
      /** @type { string } */
      this._gisVersion = gisVersion;
      /** @type { Date } */
      this._dateCréation = new Date();
      /** @type { Date } */
      this._dateSauvegarde = this._dateCréation;
      /** @type { SingleMapPage | MultiMapPage } et autres types a venir */
      this._activeMapPage = null;
      /** @type { Map < int, SingleMapPage | MultiMapPage > } et autres types a venir */
      this._mapPages = new Map([
        [1, this._activeMapPage]
      ]);
      /** @type {number} */
      this._compteurPage = 0;
    }
  }

  /*
   * GETTERS & SETTERS
   */

  get title () { return this._titre; }

  set title (newTitle) { this._titre = newTitle; }

  get gisVersion () { return this._gisVersion; }

  set gisVersion (newVersion) { this._gisVersion = newVersion; }

  get fromJson () { return this._fromJson; }

  get creationDate () { return this._dateCréation; }

  get saveDate () { return this._dateSauvegarde; }

  get pageCount () { return this._compteurPage; }

  set pageCount (count) { this._compteurPage = count; }

  get activeMapPage () { return this._activeMapPage; }

  get idProjet () { return this._idProjet; }

  set idProjet (idProjet) { this._idProjet = idProjet; }

  /*
   *  Méthodes
   */

  setActiveMapPage (pageId) {
    if (this._activeMapPage !== this._mapPages.get(pageId)) this._activeMapPage = this._mapPages.get(pageId);
    this._notifierChangementPageActive(this._activeMapPage);
  }

  /**
   * @param { string } pageName
   */
  addSingleMapPageToModel (pageName) {
    const pageId = this.pageCount;
    const mapSectionId = 'map__section-' + this.pageCount;
    const mapPage = new SingleMapPage({ id: pageId, title: pageName, targetMap: mapSectionId });
    mapPage.activer();
    this._mapPages.set(pageId, mapPage);
    this._notifierSingleMapPageAjoutée(mapPage);
    this.setActiveMapPage(pageId);
  }

  /**
   * @param { string } pageName
   */
  addMultiMapPageToModel (pageName) {
    const pageId = this.pageCount;
    const mapPage = new MultiMapPage({ id: pageId, title: pageName });
    this._mapPages.set(pageId, mapPage);
    this._notifierMultiMapPageAjoutée(mapPage);
    this.setActiveMapPage(pageId);
  }

  /**
   * @todo à completer : changement de position des tabs, tant dans la liste que à l'écran
   * @param { number } position - position de la tab à assigner (sera p-e un autre parametre)
   */
  _setPageTabPosition (position) {

  }

  /**
   * @param { number } pageId
   * @todo arranger le else ou faire comme avec les layers dans SingleMapPage
   * !! probleme ici
   */
  removeMapPage (pageId) {
    if (this._mapPages.size > 1) {
      const pageIterator = this._mapPages.keys();
      const idPage1 = pageIterator.next().value;
      const idPage2 = pageIterator.next().value;
      const firstMapId = idPage1 === pageId ? idPage2 : idPage1;
      const pageCarte = this._mapPages.get(pageId);
      this.setActiveMapPage(firstMapId);
      if (pageCarte instanceof SingleMapPage) {
        pageCarte.supprimer();
      }
      this._notifierSingleMapPageEnlevée(pageCarte);
      this._mapPages.delete(pageId);
    } else {
      console.log(this._mapPages);
      console.log('derniere carte du projet a gérer');
    }
  }

  /**
   * @param { number } position
   * @todo a compléter
   */
  setPagePosition (position) {

  }

  /**
   * @param { number  } position
   * @returns { SingleMapPage | MultiMapPage }
   */
  getMapPage (position) {
    const mapPage = this._mapPages.get(position);
    return mapPage;
  }

  /*
   *  Listeners externes
   */

  _notifierSingleMapPageAjoutée (mapPage) {}

  registerSingleMapPageAddedListener (listener) {
    this._notifierSingleMapPageAjoutée = listener;
  }

  _notifierSingleMapPageEnlevée (mapPage) {}

  registerSingleMapPageRemovedListener (listener) {
    this._notifierSingleMapPageEnlevée = listener;
  }

  _notifierMultiMapPageAjoutée (mapPage) {}

  écouterMultiMapPageAjoutée (listener) {
    this._notifierMultiMapPageAjoutée = listener;
  }

  _notifierChangementPageActive (mapPage) {}

  registerActiveMapPageListener (listener) {
    this._notifierChangementPageActive = listener;
  }

  /*
   * JSON - Sauvegarde
   */

  toJSON () {
    if (this.idProjet || typeof globalIdProjet !== 'undefined') {
      return {
        idProjet: this.idProjet || globalIdProjet,
        title: this._titre,
        gisVersion: this._gisVersion,
        creationDate: this._dateCréation.toJSON(),
        saveDate: new Date().toJSON(),
        mapPages: convertirPagesCarteEnJSON(this._mapPages),
        activeMapPageId: this._activeMapPage.id,
        pageCount: this._compteurPage
      };
    } else {
      return {
        title: this._titre,
        gisVersion: this._gisVersion,
        creationDate: this._dateCréation.toJSON(),
        saveDate: new Date().toJSON(),
        mapPages: convertirPagesCarteEnJSON(this._mapPages),
        activeMapPageId: this._activeMapPage.id,
        pageCount: this._compteurPage
      };
    }
  }

  /*
   * JSON - Chargement
   */

  _créerDepuisJSON (jsonProject) {
    this._titre = jsonProject.title;
    this._gisVersion = jsonProject.gisVersion;
    this._dateCréation = new Date(jsonProject.creationDate);
    this._dateSauvegarde = new Date(jsonProject.saveDate);
    this._mapPages = new Map();
    this._activeMapPage = null;
    this._compteurPage = jsonProject.pageCount;
    if (jsonProject.idProjet) {
      this.idProjet = jsonProject.idProjet;
    } else if (typeof globalIdProjet !== 'undefined') { // eslint-disable-line no-undef
      this.idProjet = globalIdProjet; // eslint-disable-line no-undef
    }
  }

  addSingleMapPageToModelFromJson (jsonSingleMapPage) {
    const layers = jsonSingleMapPage.layers;
    const mapPage = new SingleMapPage({ jsonSingleMapPage: jsonSingleMapPage });
    // mapPage.activer();
    this._ajouterPageCarteUniqueÀListePagesCartesDepuisJSON(mapPage, layers);
  }

  _ajouterPageCarteUniqueÀListePagesCartesDepuisJSON (mapPage, layers) {
    const _mapPage = mapPage;
    const pageId = _mapPage.id;
    this._mapPages.set(pageId, _mapPage);
    /** @type { SingleMapPage } */
    const storedMapPage = this._mapPages.get(pageId);
    storedMapPage.createLayersFromJSON(layers);
  }

  addMultiMapPageToModelFromJson (jsonMultiMapPage) {
    const singleMapPages = jsonMultiMapPage.maps;
    const mapPage = new MultiMapPage({ jsonMultiMapPage: jsonMultiMapPage });
    this._ajouterPageCartesMultiplesÀListePagesCartesDepuisJSON(mapPage, singleMapPages);
  }

  /**
   * @param { MultiMapPage } multiMapPage
   * @param { { position: number, singleMapPageId: number}[] } singleMapPages
   */
  _ajouterPageCartesMultiplesÀListePagesCartesDepuisJSON (multiMapPage, singleMapPages) {
    const _mapPage = multiMapPage;
    const pageId = _mapPage.id;
    this._mapPages.set(pageId, _mapPage);
    /** @type { MultiMapPage } */
    const _multiMapPage = this._mapPages.get(pageId);

    this._ajouterPagesCarteUniqueÀPageCartesMultiplesDepuisJSON(_multiMapPage, singleMapPages);
    this._notifierMultiMapPageAjoutée(_multiMapPage);
  }

  /**
   * @param { MultiMapPage } multiMapPage
   * @param { { position: number, singleMapPageId: number }[] } singleMapPagesJson
   */
  _ajouterPagesCarteUniqueÀPageCartesMultiplesDepuisJSON (multiMapPage, singleMapPagesJson) {
    const _this = this;
    /** @type { Map < number, SingleMapPage > } */
    const maps = new Map();

    singleMapPagesJson.forEach(function (map) {
      maps.set(map.position, _this._mapPages.get(map.singleMapPageId));
    });

    multiMapPage.maps = maps;
  }
}

/*
 * FONCTIONS
 */

/**
 * @param { Map < int, SingleMapPage | MultiMapPage > } mapPages
 * @returns { json }
 */
function convertirPagesCarteEnJSON (mapPages) {
  const mapPagesJSON = [];
  for (const [key, value] of mapPages.entries()) {
    mapPagesJSON.push([key, value.toJSON()]);
  }
  return mapPagesJSON;
}
